import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchInspectionReview } from '../api/inspections';
import { fetchInbox, type InboxRow } from '../api/tests';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';

/**
 * The session page's one line about its tests (the owner, 2026-09-29). The
 * page used to carry the whole merged timeline, with every capture as a
 * thumbnail that opened in a new tab and could not be marked, and the
 * placement table under it: on a tour's session the two were 11 784 of the
 * page's 13 487 px. What is reviewed is reviewed on /proves; what is
 * technical is on the session's own technical page, an admin's.
 *
 * An admin reads how many captures the session has and how many are marked,
 * with the way to the review and to the technical page. Anybody else reads
 * only the way to the card they took in this session, when they took one.
 * Nothing at all when there is nothing to say.
 */
export function SessionTestsCard({ code }: { code: string }) {
  const t = ca.sessionTestsCard;
  const { me } = useAuth();
  const isAdmin = me.roles.includes('ADMIN');
  const [pictures, setPictures] = useState<{ total: number; reviewed: number } | null>(null);
  const [mine, setMine] = useState<InboxRow[]>([]);

  useEffect(() => {
    let alive = true;
    setPictures(null);
    setMine([]);
    if (!me.authenticated) return;
    if (isAdmin) {
      fetchInspectionReview(code)
        .then((view) => {
          if (!alive) return;
          setPictures({
            total: view.captures.length,
            reviewed: view.captures.filter((c) => c.reviewedBy).length,
          });
        })
        .catch(() => undefined);
    } else if (me.tests) {
      fetchInbox()
        .then((inbox) => {
          if (alive) setMine(inbox.rows.filter((r) => r.sessionCode === code));
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [code, isAdmin, me.authenticated, me.tests]);

  if (!me.authenticated) return null;
  if (!isAdmin && mine.length === 0) return null;

  // A quiet row at the foot of the page, not a card among the players' own (the
  // owner, 2026-10-12): it is there for whoever tests or administers, and a
  // player's page reads the same with or without it.
  return (
    <section className="quiet-links muted small" aria-label={t.title}>
      <span className="quiet-links-title">
        <i className="bi bi-clipboard-check" aria-hidden="true" /> {t.title}
      </span>
      {isAdmin && pictures && pictures.total > 0 && (
        <span>
          {t.pictures(pictures.total, pictures.reviewed)}{' '}
          <Link to={`/proves/inspeccio/${code}`}>{t.review}</Link>
        </span>
      )}
      {mine.map((row) => (
        <Link key={row.key} to={row.link}>
          {row.title ?? t.yourTest}
        </Link>
      ))}
      {isAdmin && (
        <Link to={`/s/${code}/tecnic`}>
          <i className="bi bi-tools" aria-hidden="true" /> {t.technical}
        </Link>
      )}
    </section>
  );
}
